import { Icon, StatusProxy } from "@focus/focusUI";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { InputOrbitCardList } from "../components/orbits/InputOrbitCardList";
import { InputOrbitLogsWindow } from "../components/orbits/InputOrbitLogs/InputOrbitLogsWindow";
import { InputOrbitTable } from "../components/orbits/InputOrbitTable";
import type { TableActionType } from "../components/table/TableActionColumn";
import { TableShell } from "../components/table/TableShell";
import {
  orbitColumnsLayout,
  useSavedOrbitColumns,
} from "../services/configuration";
import { OPS_ACTIVE_HEADERS } from "../services/conjunctions/Headers";
import { downloadFile } from "../services/downloaduploadOrbits/actions";
import { useUploadedOrbitsReadyStatusKey } from "../services/downloaduploadOrbits/hooks";
import { type ServerColumnConfig } from "../services/helpers/headers";
import type { ColumnFiltersState } from "../services/helpers/pager";
import {
  INPUT_STATE_FILTER_ID,
  type InputOrbitRow,
} from "../services/inputs";
import { orbits } from "../services/orbits";
import type { RootState } from "../store";
import {
  ACTION_ICON_CLASS_NAME,
  canSetInputFilePending,
  DISABLED_ACTION_ICON_CLASS_NAME,
  getComputedInputState,
  getInputsColumnDefinitions,
  getOrbitStatusDisplay,
  inputStatePillClassName,
  inputStateSelectedClassName,
  LINKED_OEM_ACTION_CONFIG,
  setInputFilePending,
  STATUS_BUTTON_CLASS_NAME,
} from "../utils/inputsOrbits";
import { InputsState, type InputsStateType } from "../types/inputs";
interface InputOrbitActionData {
  row: {
    original: InputOrbitRow;
  };
}

interface InputOrbitActionContext {
  base: string;
  downloadData: (payload: { id: string }) => void;
}

type LinkedOemAction = "info" | "download" | "plots";
type InputsViewMode = "table" | "cards";

const viewModeOptions: Array<{
  icon: "th-list" | "grid-view";
  label: string;
  value: InputsViewMode;
}> = [
  { icon: "grid-view", label: "Cards", value: "cards" },
  { icon: "th-list", label: "Table", value: "table" },
];

const renderDisabledActionIcon = ({
  icon,
  keyPrefix,
  rowId,
  title,
}: {
  icon: (typeof LINKED_OEM_ACTION_CONFIG)[LinkedOemAction]["icon"];
  keyPrefix: string;
  rowId: string;
  title: string;
}) => (
  <Icon
    key={`icon-${keyPrefix}-${rowId}`}
    size={16}
    icon={icon}
    title={title}
    className={DISABLED_ACTION_ICON_CLASS_NAME}
  />
);

const renderStatusAction =
  (onSetPending: (payload: { id: string }) => void): TableActionType =>
  (data: InputOrbitActionData) => {
    const rowData = data.row.original;
    const statusDisplay = getOrbitStatusDisplay(rowData);
    const statusIcon = (
      <Icon
        size={16}
        icon={statusDisplay.icon}
        color={statusDisplay.color}
        title={statusDisplay.title}
      />
    );

    if (!canSetInputFilePending(rowData)) {
      return (
        <span
          key={`icon-pending-${rowData.id}`}
          aria-label={statusDisplay.title}
          className="flex rounded-md p-1.5"
          title={statusDisplay.title}
        >
          {statusIcon}
        </span>
      );
    }

    return (
      <button
        key={`icon-pending-${rowData.id}`}
        type="button"
        aria-label="Set as pending for rescreening"
        title="Set as pending for rescreening"
        onClick={() => onSetPending({ id: rowData.id })}
        className={STATUS_BUTTON_CLASS_NAME}
      >
        {statusIcon}
      </button>
    );
  };

const renderLogAction =
  (onViewLog: (rowData: InputOrbitRow) => void): TableActionType =>
  (data: InputOrbitActionData) => {
    const rowData = data.row.original;

    return (
      <Icon
        key={`icon-log-${rowData.id}`}
        size={16}
        icon="document-open"
        title="View log"
        onClick={() => onViewLog(rowData)}
        className={ACTION_ICON_CLASS_NAME}
      />
    );
  };

const renderLinkedOemInfoAction = (
  rowId: string,
  parentOemId: string,
  context: InputOrbitActionContext,
) => (
  <Link
    to={`/${context.base}/${parentOemId}/info`}
    key={`link-info-parent-oem-${rowId}`}
    className="flex"
  >
    <Icon
      size={16}
      icon="eye-open"
      title="See details"
      className={ACTION_ICON_CLASS_NAME}
    />
  </Link>
);

const renderLinkedOemDownloadAction = (
  rowId: string,
  parentOemId: string,
  context: InputOrbitActionContext,
) => (
  <Icon
    key={`icon-download-${rowId}`}
    size={16}
    icon="download"
    title="Download file"
    onClick={() => context.downloadData({ id: parentOemId })}
    className={ACTION_ICON_CLASS_NAME}
  />
);

const renderLinkedOemPlotsAction = (
  rowData: InputOrbitRow,
  parentOemId: string,
  context: InputOrbitActionContext,
) => {
  if (rowData.linkedOEM?.plotFiles !== "AVAILABLE") {
    return renderDisabledActionIcon({
      icon: LINKED_OEM_ACTION_CONFIG.plots.icon,
      keyPrefix: "plot",
      rowId: rowData.id,
      title: "No plots available",
    });
  }

  return (
    <Link
      to={`/${context.base}/${parentOemId}/info?tab=Plots`}
      key={`link-plot-parent-oem-${rowData.id}`}
      className="flex"
    >
      <Icon
        size={16}
        icon="timeline-line-chart"
        title="See plots"
        className={ACTION_ICON_CLASS_NAME}
      />
    </Link>
  );
};

const renderUnavailableLinkedOemAction = (
  rowId: string,
  action: LinkedOemAction,
) =>
  renderDisabledActionIcon({
    icon: LINKED_OEM_ACTION_CONFIG[action].icon,
    keyPrefix: action,
    rowId,
    title: LINKED_OEM_ACTION_CONFIG[action].noOrbitTitle,
  });

const renderLinkedOemAction =
  (action: LinkedOemAction): TableActionType =>
  (data: InputOrbitActionData, context: InputOrbitActionContext) => {
    const rowData = data.row.original;
    const parentOemId = rowData.linkedOEM?.id;

    if (!parentOemId) {
      return renderUnavailableLinkedOemAction(rowData.id, action);
    }

    switch (action) {
      case "info":
        return renderLinkedOemInfoAction(rowData.id, parentOemId, context);
      case "download":
        return renderLinkedOemDownloadAction(rowData.id, parentOemId, context);
      case "plots":
        return renderLinkedOemPlotsAction(rowData, parentOemId, context);
    }
  };
/**
 * Container for the Orbits table in the Inputs section.
 *
 * It fetches paginated data, manages table filters/layout, and wires row actions
 * to the linked OEM when an input has one.
 */
export const InputsOrbitsContainer = () => {
  const dispatch = useDispatch();
  const savedColumnsLayout = useSavedOrbitColumns();
  const uploadedOrbitsRefreshKey = useUploadedOrbitsReadyStatusKey();
  const [pendingStatusRefreshKey, setPendingStatusRefreshKey] = useState(0);

  const [selectedLogRow, setSelectedLogRow] = useState<InputOrbitRow | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<InputsViewMode>("cards");

  const [selectedState, setSelectedState] = useState<InputsStateType | null>(
    null,
  );
  
  const orbitsFilter = useSelector(
    (state: RootState) => state.orbits.filter,
    shallowEqual,
  );

  useEffect(() => {
    const stateFilter = orbitsFilter.find(
      (filter) => filter.id === INPUT_STATE_FILTER_ID,
    );
    const nextSelectedState = Object.values(InputsState).includes(
      stateFilter?.value as InputsStateType,
    )
      ? (stateFilter?.value as InputsStateType)
      : null;

    setSelectedState((current) =>
      current === nextSelectedState ? current : nextSelectedState,
    );
  }, [orbitsFilter]);

  const columnDefinitions = useMemo(getInputsColumnDefinitions, []);
  const handleSetInputFilePending = useCallback(
    (payload: { id: string }) => {
      void setInputFilePending(payload, dispatch, () => {
        setPendingStatusRefreshKey((current) => current + 1);
      });
    },
    [dispatch],
  );
  const tableActions = useMemo<TableActionType[]>(
    () => [
      renderLinkedOemAction("download"),
      renderLinkedOemAction("info"),
      renderLinkedOemAction("plots"),
      renderLogAction(setSelectedLogRow),
      renderStatusAction(handleSetInputFilePending),
    ],
    [handleSetInputFilePending],
  );
  const saveColumnLayout = useCallback(
    (serverConfig: ServerColumnConfig) => {
      dispatch(orbitColumnsLayout.actions.saveItems(serverConfig));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (filtered: ColumnFiltersState) => {
      dispatch(orbits.actions.setFilter(filtered));
    },
    [dispatch],
  );

  const handleDownload = useCallback(
    (payload: { id: string }) => {
      dispatch(downloadFile(payload));
    },
    [dispatch],
  );

  const refreshKey = useMemo(
    () => `${uploadedOrbitsRefreshKey}:${pendingStatusRefreshKey}`,
    [pendingStatusRefreshKey, uploadedOrbitsRefreshKey],
  );
  const handleSelectedState = useCallback(
    (record: InputsStateType) => {
      setSelectedState((current) => {
        const nextState = current === record ? null : record;

        dispatch(
          orbits.actions.setFilter([
            ...orbitsFilter.filter(
              (filter) => filter.id !== INPUT_STATE_FILTER_ID,
            ),
            ...(nextState
              ? [
                  {
                    id: INPUT_STATE_FILTER_ID,
                    operator: "equals",
                    value: nextState,
                  },
                ]
              : []),
          ]),
        );

        return nextState;
      });
    },
    [dispatch, orbitsFilter],
  );
  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <StatusProxy
        status={savedColumnsLayout.status}
        error={savedColumnsLayout.error}
      >
        <div className="flex-1 min-h-0 overflow-auto">
          <TableShell
            className="conjunctions-table-shell shrink-0"
            header={
              <header className="flex flex-nowrap items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4">
                <div className="focusoc-form-shell flex min-w-0 flex-nowrap items-center gap-3">
                  <div className="flex flex-nowrap items-center gap-2 text-xs font-semibold uppercase">
                    {(Object.values(InputsState) as InputsStateType[]).map(
                      (state) => {
                        const isSelected = selectedState === state;
                        return (
                          <span
                            aria-pressed={isSelected}
                            className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 transition-all duration-150 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${inputStatePillClassName[state]} ${
                              isSelected
                                ? `${inputStateSelectedClassName[state]} scale-105 shadow-sm`
                                : "opacity-55 hover:opacity-100"
                            }`}
                            key={state}
                            onClick={() => handleSelectedState(state)}
                            role="button"
                            tabIndex={0}
                          >
                            {state.toLowerCase().replaceAll("_", " ")}
                          </span>
                        );
                      },
                    )}
                  </div>
                </div>

                <div className="focusoc-form-shell flex flex-nowrap items-center justify-end gap-3">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold uppercase text-slate-500">
                      View
                    </span>

                    <div
                      aria-label="Inputs orbits view"
                      className="inline-flex h-9 flex-nowrap items-center rounded-lg border border-slate-200 bg-slate-50 p-1"
                      role="group"
                    >
                      {viewModeOptions.map((option) => {
                        const isSelected = viewMode === option.value;

                        return (
                          <button
                            aria-pressed={isSelected}
                            className={`inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-sm font-semibold transition ${
                              isSelected
                                ? "bg-white text-slate-950 shadow-sm"
                                : "text-slate-500 hover:text-slate-950"
                            }`}
                            key={option.value}
                            onClick={() => setViewMode(option.value)}
                            title={`Show ${option.label.toLowerCase()} view`}
                            type="button"
                          >
                            <Icon icon={option.icon} size={14} title={false} />
                            <span>{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </header>
            }
          >
            {viewMode === "table" ? (
              <InputOrbitTable
                columnDefinitions={columnDefinitions}
                serverDisplayConfig={savedColumnsLayout.items}
                defaultVisibleValues={OPS_ACTIVE_HEADERS}
                base="orbits"
                actions={tableActions}
                downloadData={handleDownload}
                controlledColumnFilter={orbitsFilter}
                controlledColumnFilterChange={handleFilterChange}
                initialSorting={[{ id: "createdAt", desc: true }]}
                saveColumnLayout={saveColumnLayout}
                refreshKey={refreshKey}
                showOperatorColumn={false}
                rowClassRules={{
                  "!bg-green-50": (params) =>
                    getComputedInputState(params.original) ===
                    InputsState.ACTIVE,
                  "!bg-amber-50": (params) =>
                    getComputedInputState(params.original) ===
                    InputsState.ABOUT_TO_EXPIRE,
                    "!bg-blue-50": (params) =>
                    getComputedInputState(params.original) ===
                    InputsState.TEST,
                }}
              />
            ) : (
              <InputOrbitCardList
                actions={tableActions}
                base="orbits"
                downloadData={handleDownload}
                filter={orbitsFilter}
                refreshKey={refreshKey}
              />
            )}
          </TableShell>
        </div>

        <InputOrbitLogsWindow
          input={selectedLogRow}
          onClose={() => setSelectedLogRow(null)}
        />
      </StatusProxy>
    </div>
  );
};
